import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AuthedImage } from "@/components/SaleDetailDialog";
import { fmtDate, fmtNum, groupByBasket } from "@/lib/helpers";
import { IdCard, Search, RotateCcw, Eye } from "lucide-react";

const STATUS_CLS = {
  tamam: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  beklemede: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  eksik: "bg-slate-700 text-slate-300 border-slate-600",
  sorunlu: "bg-red-500/20 text-red-300 border-red-500/40",
};

const emptyFilters = { customer: "", product: "", start_date: "", end_date: "", branch_id: "", user_id: "" };
const selCls = "w-full h-10 bg-slate-950/50 border border-slate-700 rounded-md px-3 text-sm";

function Filters({ f, setF, branches, users, isMudur, onSearch, onReset }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass">
      <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div><Label className="text-xs">Müşteri Ad Soyad</Label>
          <Input value={f.customer} onChange={set("customer")} placeholder="Örn: Ahmet" className="bg-slate-950/50 border-slate-700" data-testid="archive-filter-customer" /></div>
        <div><Label className="text-xs">Ürün Adı</Label>
          <Input value={f.product} onChange={set("product")} placeholder="Örn: Fiber" className="bg-slate-950/50 border-slate-700" data-testid="archive-filter-product" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Başlangıç</Label>
            <Input type="date" value={f.start_date} onChange={set("start_date")} className="bg-slate-950/50 border-slate-700" data-testid="archive-filter-start" /></div>
          <div><Label className="text-xs">Bitiş</Label>
            <Input type="date" value={f.end_date} onChange={set("end_date")} className="bg-slate-950/50 border-slate-700" data-testid="archive-filter-end" /></div>
        </div>
        {!isMudur && (
          <div><Label className="text-xs">Şube</Label>
            <select value={f.branch_id} onChange={set("branch_id")} className={selCls} data-testid="archive-filter-branch">
              <option value="">Tüm şubeler</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select></div>
        )}
        <div><Label className="text-xs">Personel</Label>
          <select value={f.user_id} onChange={set("user_id")} className={selCls} data-testid="archive-filter-user">
            <option value="">Tüm personel</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select></div>
        <div className="flex items-end gap-2">
          <Button onClick={onSearch} className="flex-1 bg-blue-600 hover:bg-blue-500" data-testid="archive-search"><Search size={16} className="mr-1" /> Filtrele</Button>
          <Button variant="outline" onClick={onReset} className="border-slate-700" data-testid="archive-reset"><RotateCcw size={16} /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewDialog({ group, onClose }) {
  const sale = group?.first;
  const base = sale ? `${(sale.customer_name || "musteri").replace(/\s+/g, "_")}_${sale.created_at.slice(0, 10)}` : "";
  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="archive-preview-dialog">
        {sale && (
          <>
            <DialogHeader>
              <DialogTitle>{sale.customer_name || "Müşteri adı yok"}</DialogTitle>
              <p className="text-xs text-slate-400">{group.code ? `${group.code} · ` : ""}{sale.user_name} · {sale.branch_name} · {fmtDate(sale.created_at)}</p>
            </DialogHeader>
            <div className="rounded-xl border border-slate-800 divide-y divide-slate-800" data-testid="archive-preview-products">
              {group.items.map((x) => (
                <div key={x.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-semibold">{x.product_name}</span>
                  <span className="font-mono text-slate-400">{fmtNum(x.quantity)} adet · <span className="text-amber-300">{fmtNum(x.points)}p</span></span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[["Ön Yüz", sale.id_card_front_path, "on"], ["Arka Yüz", sale.id_card_back_path, "arka"]].map(([label, path, suf]) => (
                <div key={suf} data-testid={`archive-preview-${suf}`}>
                  <div className="text-[10px] uppercase font-mono text-slate-500 mb-1">{label}</div>
                  {path ? <AuthedImage path={path} downloadName={`${base}_${suf}`} />
                    : <div className="aspect-[16/10] rounded-xl bg-slate-800/60 border border-slate-700 flex items-center justify-center text-xs text-slate-500">Yüklenmemiş</div>}
                </div>
              ))}
            </div>
            {sale.customer_note && <p className="text-sm text-slate-300 mt-2">Not: {sale.customer_note}</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function IdArchive() {
  const { user } = useAuth();
  const isMudur = user?.role === "mudur";
  const [f, setF] = useState(emptyFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [preview, setPreview] = useState(null);
  const groups = groupByBasket(rows);

  const load = async (filters) => {
    setLoading(true);
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    const r = await http.get(`/id-archive?${params.toString()}`).finally(() => setLoading(false));
    setRows(r.data);
  };

  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales"));
  const fRef = useRef(f);
  fRef.current = f;
  useEffect(() => { if (tick) load(fRef.current); }, [tick]); // eslint-disable-line
  useEffect(() => {
    load(emptyFilters);
    http.get("/branches").then((r) => setBranches(r.data));
    http.get("/users").then((r) => setUsers(r.data.filter((u) => ["personel", "mudur"].includes(u.role))));
  }, []);

  return (
    <div className="space-y-6">
      <header className="animate-fade-up">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><IdCard className="text-blue-400" /> Kimlik Arşivi</h1>
        <p className="text-slate-400 text-sm mt-1">Satışlarla birlikte yüklenen kimlik fotoğraflarına geriye dönük erişin</p>
      </header>

      <Filters f={f} setF={setF} branches={branches} users={users} isMudur={isMudur}
               onSearch={() => load(f)} onReset={() => { setF(emptyFilters); load(emptyFilters); }} />

      <Card className="border-slate-800 bg-slate-900/60 glass">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 text-xs font-mono text-slate-500" data-testid="archive-count">{loading ? "Yükleniyor..." : `${groups.length} kayıt`}</div>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead>Tarih</TableHead><TableHead>Müşteri</TableHead><TableHead>Ürün</TableHead>
                <TableHead>Personel</TableHead><TableHead>Şube</TableHead><TableHead>Durum</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => { const s = g.first; return (
                <TableRow key={g.key} className="border-slate-800 cursor-pointer hover:bg-slate-800/40" onClick={() => setPreview(g)} data-testid={`archive-row-${g.key}`}>
                  <TableCell className="text-xs font-mono whitespace-nowrap">{fmtDate(s.created_at)}</TableCell>
                  <TableCell className="font-semibold">{s.customer_name || <span className="text-slate-500 italic">—</span>}</TableCell>
                  <TableCell className="text-sm">{g.items.length > 1 ? `${g.items.length} ürün: ${g.items.map((x) => x.product_name).join(", ")}` : s.product_name}</TableCell>
                  <TableCell className="text-sm">{s.user_name}</TableCell>
                  <TableCell className="text-sm">{s.branch_name}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_CLS[s.id_card_status] || ""}>{s.id_card_status}</Badge></TableCell>
                  <TableCell><Button size="sm" variant="ghost" data-testid={`archive-view-${g.key}`}><Eye size={16} /></Button></TableCell>
                </TableRow>
              ); })}
              {!loading && groups.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Kayıt bulunamadı</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PreviewDialog group={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
